// frontend/src/components/charts/barEnvelopeData.js
//
// The timeline payload → chart shape reader for BarEnvelopeChart.

import { withGroupNames } from '../../utils/chartGroups';
import { isReadablePeriod } from '../../utils/chartPeriods';

/**
 * Every empty reason the chart can show, with its own sentence, or null for
 * the caller's no-data text: the endpoints' vocabulary
 * (backend/reporting/test_timeline_contract.py) plus `bad_payload`.
 */
export const EMPTY_REASON_KEYS = {
  no_data: null,
  no_data_in_filter: 'charts.common.noDataInFilter',
  orders_not_synced: 'charts.common.ordersNotSynchronized',
  inventory_not_synced: 'charts.common.inventoryNotSynchronized',
  no_cost_data: 'charts.common.noCostData',
  bad_payload: 'charts.common.dataUnavailable',
};

/**
 * Read a timeline payload into the things the chart renders from.
 *
 *   `today_period`   the bucket the store is currently in, in store-local time
 *   `has_projection` whether the series carries a projected half from `today_period` on
 *
 * Every group series the payload carries (`groups`, and `forecast_groups`
 * when present) comes out named: the wire's null label, the items with no
 * product category, becomes `uncategorizedLabel` here, once, so the bars,
 * the forecast lines and the envelope key the same group by the same name.
 *
 * @param {object|null} fetchedData — the raw payload.
 * @param {string} uncategorizedLabel — the merchant's word for the unlabelled group.
 * @returns {{historyData: object|null, projectionData: object|null,
 *   currentPeriod: string, allPeriods: string[], hasHistory: boolean,
 *   emptyReason: string|null}}
 *   `emptyReason` non-null means "draw nothing, say this instead".
 *   `hasHistory` is whether the axis holds a bucket before the current one.
 */
export function normalizeTimelinePayload(fetchedData, uncategorizedLabel) {
  const empty = {
    historyData: null, projectionData: null,
    currentPeriod: '', allPeriods: [], hasHistory: false, emptyReason: null,
  };

  if (!fetchedData?.groups || fetchedData.groups.length === 0) {
    const reason = fetchedData?.empty_reason ?? null;
    if (reason !== null && !Object.hasOwn(EMPTY_REASON_KEYS, reason)) {
      console.error('BarEnvelopeChart: payload carries an unknown empty_reason', { empty_reason: reason });
      return { ...empty, emptyReason: 'bad_payload' };
    }
    return { ...empty, emptyReason: reason };
  }

  const periods = fetchedData.periods || [];
  const todayPeriod = fetchedData.today_period;
  const hasProjection = fetchedData.has_projection;

  if (typeof hasProjection !== 'boolean' || !todayPeriod) {
    console.error('BarEnvelopeChart: payload is missing today_period / has_projection', {
      today_period: todayPeriod, has_projection: hasProjection,
    });
    return { ...empty, emptyReason: 'bad_payload' };
  }

  const unreadable = [todayPeriod, ...periods].find(period => !isReadablePeriod(period));
  if (unreadable !== undefined) {
    console.error('BarEnvelopeChart: payload carries an unreadable period', { period: unreadable });
    return { ...empty, emptyReason: 'bad_payload' };
  }

  const named = {
    ...fetchedData,
    groups: withGroupNames(fetchedData.groups, uncategorizedLabel),
    ...(fetchedData.forecast_groups
      ? { forecast_groups: withGroupNames(fetchedData.forecast_groups, uncategorizedLabel) }
      : {}),
  };
  return {
    historyData: named,
    projectionData: hasProjection ? named : null,
    currentPeriod: todayPeriod,
    allPeriods: periods,
    hasHistory: periods.some(period => period < todayPeriod),
    emptyReason: null,
  };
}

export default normalizeTimelinePayload;
